import React, { useState } from 'react';
import './PriceCalculator.css';

const PriceCalculator = () => {
    const [pages, setPages] = useState(1);
    const [features, setFeatures] = useState({
        contactForm: false,
        map: false,
        calendar: false,
        gallery: false,
        googleListing: false,
        facebookPage: false,
        restaurantMenu: false // Added back based on FEATURE_PRICES and featureLabels
    });
    const [extraMenuCount, setExtraMenuCount] = useState(0);

    // Base price for setup and design
    const BASE_PRICE = 250;
    const PRICE_PER_PAGE = 40;

    const FEATURE_PRICES = {
        contactForm: 15,
        map: 5,
        calendar: 15,
        gallery: 25,
        restaurantMenu: 50,
        googleListing: 25,
        facebookPage: 25
    };
    const EXTRA_MENU_PRICE = 15;
    const YEARLY_FEE = 150;

    const featureLabels = {
        contactForm: "Contact Form",
        map: "Interactive Map",
        calendar: "Calendar",
        gallery: "Photo Gallery",
        restaurantMenu: "Restaurant Menus (Includes 2)",
        googleListing: "Setup Google Listing",
        facebookPage: "Setup Facebook Page"
    };

    // Helper object to identify which features have extra options
    const hasExtraOptions = {
        restaurantMenu: true
    };

    const calculateOneTime = () => {
        // Base price includes 1 page, so we only charge for additional pages
        const additionalPages = Math.max(0, pages - 1);
        let total = BASE_PRICE + (additionalPages * PRICE_PER_PAGE);

        Object.keys(features).forEach(feature => {
            if (features[feature]) {
                total += FEATURE_PRICES[feature];
            }
        });

        if (features.restaurantMenu) {
            total += extraMenuCount * EXTRA_MENU_PRICE;
        }

        return total;
    };


    const handleFeatureClick = (feature) => {
        setFeatures(prev => {
            const newState = { ...prev, [feature]: !prev[feature] };
            // Reset extra count if turning off
            if (feature === 'restaurantMenu' && !newState[feature]) {
                setExtraMenuCount(0);
            }
            return newState;
        });
    };

    const incrementExtraMenu = (e) => {
        e.stopPropagation();
        setExtraMenuCount(prev => prev + 1);
    };

    const decrementExtraMenu = (e) => {
        e.stopPropagation();
        setExtraMenuCount(prev => (prev > 0 ? prev - 1 : 0));
    };

    return (
        <div className="price-calculator-container">
            <div className="price-calculator">
                <div className="calculator-header">
                    <h3>Website Price Estimator</h3>
                    <p>Select features to get a rough estimate for your project</p>
                </div>

                <div className="base-package-includes">
                    <h4>Included in Base Package (${BASE_PRICE})</h4>
                    <ul>
                        <li><span className="icon">📄</span> 1 Page Website</li>
                        <li><span className="icon">🖼️</span> Top Banner (1 Image)</li>
                        <li><span className="icon">📍</span> Contact Info, Hours & Address</li>
                        <li><span className="icon">🛠️</span> Services Section</li>
                        <li><span className="icon">🔗</span> Social Media Links</li>
                        <li><span className="icon">📷</span> 2 Additional Images (Interior/Product)</li>
                        <li className="highlight-item"><span className="icon">📅</span> 1 Year Maintenance & Hosting Included!</li>
                    </ul>
                </div>

                <div className="calculator-content">
                    <div className="calc-section page-slider">
                        <div className="slider-header">
                            <label>Number of Pages</label>
                            <span className="page-count">{pages}</span>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="20"
                            value={pages}
                            onChange={(e) => setPages(parseInt(e.target.value))}
                            className="range-slider"
                        />
                        <div className="slider-labels">
                            <span>1</span>
                            <span>20</span>
                        </div>
                        <p className="price-note text-center mt-2 text-gray-400">
                            ${PRICE_PER_PAGE}/additional page (First page included)
                        </p>
                    </div>

                    <div className="calc-section features-list">
                        <h4>Add-on Features</h4>
                        <div className="features-grid">
                            {Object.keys(features).map(key => (
                                <div
                                    key={key}
                                    className={`feature-item ${features[key] ? 'selected' : ''}`}
                                    onClick={() => handleFeatureClick(key)}
                                >
                                    <div className="feature-main">
                                        <div className="checkbox-custom">
                                            {features[key] && <span className="checkmark">✓</span>}
                                        </div>
                                        <div className="feature-info">
                                            <span className="feature-name">{featureLabels[key]}</span>
                                            <span className="feature-price">+${FEATURE_PRICES[key]}</span>
                                        </div>
                                    </div>

                                    {/* Special sub-section for Restaurant Menus */}
                                    {key === 'restaurantMenu' && features[key] && (
                                        <div className="feature-extras" onClick={(e) => e.stopPropagation()}>
                                            <div className="extra-label">
                                                Additional Menus <br />
                                                <span className="extra-subtext">(Drink, Breakfast, etc.)</span>
                                            </div>
                                            <div className="extra-controls">
                                                <button className="extra-btn" onClick={decrementExtraMenu}>-</button>
                                                <span className="extra-count">{extraMenuCount}</span>
                                                <button className="extra-btn" onClick={incrementExtraMenu}>+</button>
                                            </div>
                                            <div className="extra-price">+${EXTRA_MENU_PRICE} ea</div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="calc-section maintenance-section">
                            <h4>Annual Renewal</h4>
                            <p className="maintenance-desc">After the first year, a yearly fee covers hosting, domain renewal, and content updates.</p>

                            <div className="billing-info-card">
                                <div className="billing-header">
                                    <span className="billing-name">Yearly Maintenance</span>
                                    <span className="billing-price">${YEARLY_FEE}/yr</span>
                                </div>
                                <span className="billing-note">Starts after Year 1</span>
                            </div>
                        </div>

                        <div className="total-section">
                            <div className="price-row">
                                <span>Estimated Initial Cost:</span>
                                <span className="price-amount">${calculateOneTime()}</span>
                            </div>

                            <p className="disclaimer">*Includes design, development, and first year of hosting & maintenance.</p>

                            <a
                                href={`mailto:JacobW@segotw.com?subject=Website Quote Request (Initial: $${calculateOneTime()}, Renewal: $${YEARLY_FEE}/yr)`}
                                className="btn btn-primary quote-btn"
                            >
                                Request Official Quote
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PriceCalculator;
